import React, { useState, useCallback } from 'react';
import { 
    Box, Typography, Container, Grid, Paper, Button, 
    TextField, Switch, FormControlLabel, useTheme, 
    Divider, Card, CardContent, IconButton, Tabs, Tab
} from '@mui/material';
import { 
    Palette as PaletteIcon, 
    Image as ImageIcon, 
    CloudUpload as CloudUploadIcon, 
    CheckCircle as CheckCircleIcon,
    ColorLens as ColorLensIcon
} from '@mui/icons-material';

// --- MOCK UTILITY COMPONENTS ---

// 1. Color Picker Placeholder (Functional State Management)
const ColorSetting = ({ label, value, onChange }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, p: 1, borderBottom: '1px solid #eee' }}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>{label}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
                value={value}
                onChange={(e) => onChange(e.target.value)}
                size="small"
                sx={{ width: 120, mr: 1 }}
                inputProps={{ style: { textTransform: 'uppercase' } }}
            />
            <input
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                style={{ width: '40px', height: '40px', border: 'none', padding: 0, cursor: 'pointer' }}
            />
        </Box>
    </Box>
);

// 2. Mock Logo Upload Dropzone (Functional Drag-and-Drop)
const LogoUploadArea = ({ label, currentLogo, onUpload }) => {
    const [dragging, setDragging] = useState(false);
    
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragging(true);
        } else if (e.type === "dragleave" || e.type === "drop") {
            setDragging(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            onUpload(e.dataTransfer.files[0]);
            e.dataTransfer.clearData();
        }
    };

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>{label}</Typography>
                <Box
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    sx={{
                        border: `2px dashed ${dragging ? '#0069D1' : '#ccc'}`,
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: dragging ? '#E3F2FD' : '#fafafa',
                        transition: 'all 0.3s'
                    }}
                >
                    <input type="file" onChange={(e) => onUpload(e.target.files[0])} style={{ display: 'none' }} id={`file-upload-${label}`} accept="image/*" />
                    <label htmlFor={`file-upload-${label}`}>
                        <CloudUploadIcon sx={{ fontSize: 40, color: '#0069D1' }} />
                        <Typography variant="subtitle1" color="textSecondary">
                            {dragging ? "Drop here!" : "Drag 'n' drop or click to upload"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">(PNG/SVG recommended)</Typography>
                    </label>
                </Box>
                
                {currentLogo && (
                    <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="success.main">
                            <CheckCircleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                            **File Uploaded:** {currentLogo.name}
                        </Typography>
                        <Button size="small" variant="text" color="error" onClick={() => onUpload(null)}>Remove</Button>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

// --- MAIN COMPONENT ---
const CustomBranding = () => {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState(0);

    // State for Color Management
    const [colors, setColors] = useState({
        primary: '#0069D1',
        secondary: theme.palette.secondary.main, // Using theme default as a starting point
        background: '#F4F6F8',
    });

    // State for Asset Management
    const [assets, setAssets] = useState({
        mainLogo: null,
        favicon: null,
    });

    // State for Feature Toggles
    const [settings, setSettings] = useState({
        enableLoginBranding: true,
        useDarkTheme: false,
    });

    const handleColorChange = (key, value) => {
        setColors(prev => ({ ...prev, [key]: value.toUpperCase() }));
    };

    const handleAssetUpload = (key, file) => {
        setAssets(prev => ({ ...prev, [key]: file }));
    };

    const handleSettingToggle = (key) => {
        setSettings(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSaveBranding = useCallback(() => {
        console.log("Saving Branding Settings:", { colors, assets, settings });
        alert('Branding settings saved successfully! (Mock Action)');
    }, [colors, assets, settings]);

    // --- Tab Panels ---

    const ColorThemePanel = () => (
        <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main, display: 'flex', alignItems: 'center' }}>
                        <ColorLensIcon sx={{ mr: 1 }} />
                        Core Color Palette
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <ColorSetting 
                        label="Primary Color" 
                        value={colors.primary} 
                        onChange={(val) => handleColorChange('primary', val)} 
                    />
                    <ColorSetting 
                        label="Secondary Color" 
                        value={colors.secondary} 
                        onChange={(val) => handleColorChange('secondary', val)} 
                    />
                    <ColorSetting 
                        label="Background Color" 
                        value={colors.background} 
                        onChange={(val) => handleColorChange('background', val)} 
                    />
                </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                    <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
                        Preview
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ 
                        p: 3, 
                        minHeight: 150, 
                        borderRadius: 2, 
                        backgroundColor: colors.background, 
                        border: `2px solid ${colors.primary}`,
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Typography variant="h5" sx={{ color: colors.primary, fontWeight: 700 }}>
                            Preview Title
                        </Typography>
                        <Button variant="contained" sx={{ mt: 1, backgroundColor: colors.secondary, '&:hover': { backgroundColor: colors.secondary } }}>
                            Preview Button
                        </Button>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );

    const AssetManagementPanel = () => (
        <Grid container spacing={4}>
            <Grid item xs={12} sm={6}>
                <LogoUploadArea 
                    label="Application Logo (Header)" 
                    currentLogo={assets.mainLogo} 
                    onUpload={(file) => handleAssetUpload('mainLogo', file)} 
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <LogoUploadArea 
                    label="Favicon (.ico, 32x32)" 
                    currentLogo={assets.favicon} 
                    onUpload={(file) => handleAssetUpload('favicon', file)} 
                />
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
                        Branding Toggles
                    </Typography>
                    <FormControlLabel
                        control={
                            <Switch 
                                checked={settings.enableLoginBranding} 
                                onChange={() => handleSettingToggle('enableLoginBranding')} 
                                color="primary"
                            />
                        }
                        label="Enable custom branding on Login/Sign-up Pages"
                        sx={{ mb: 1 }}
                    />
                    <Typography variant="caption" display="block" color="textSecondary" sx={{ ml: 4, mb: 2 }}>
                        Applies primary color, secondary color, and main logo to authentication screens.
                    </Typography>
                    
                    <FormControlLabel
                        control={
                            <Switch 
                                checked={settings.useDarkTheme} 
                                onChange={() => handleSettingToggle('useDarkTheme')} 
                                color="secondary"
                            />
                        }
                        label="Enable Dark Theme Mode for users"
                        sx={{ mb: 1 }}
                    />
                </Paper>
            </Grid>
        </Grid>
    );

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom 
                sx={{ 
                    fontWeight: 700, 
                    color: theme.palette.primary.dark,
                    display: 'flex',
                    alignItems: 'center',
                    mb: 3
                }}
            >
                <ImageIcon sx={{ mr: 1, fontSize: '2.5rem', color: theme.palette.secondary.main }} />
                Custom Branding & UI Settings
            </Typography>

            <Paper elevation={4} sx={{ p: 2 }}>
                {/* Tabs for Organization */}
                <Tabs 
                    value={tabValue} 
                    onChange={(e, val) => setTabValue(val)} 
                    indicatorColor="primary" 
                    textColor="primary"
                    sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
                >
                    <Tab label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PaletteIcon sx={{ mr: 1 }} /> Color Theme
                        </Box>
                    } />
                    <Tab label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ImageIcon sx={{ mr: 1 }} /> Logos & Assets
                        </Box>
                    } />
                </Tabs>

                {/* Tab Content */}
                <Box sx={{ p: 1 }}>
                    {tabValue === 0 && <ColorThemePanel />}
                    {tabValue === 1 && <AssetManagementPanel />}
                </Box>
            </Paper>

            {/* Sticky Save Bar (Pro-level UX) */}
            <Box 
                sx={{ 
                    position: 'fixed', 
                    bottom: 0, 
                    left: 0, 
                    right: 0, 
                    backgroundColor: theme.palette.background.paper, 
                    borderTop: `1px solid ${theme.palette.divider}`,
                    zIndex: 1000,
                    py: 2,
                    px: { xs: 2, sm: 4 },
                    display: 'flex',
                    justifyContent: 'flex-end',
                    boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
                }}
            >
                <Button 
                    variant="contained" 
                    color="primary" 
                    size="large" 
                    onClick={handleSaveBranding}
                    startIcon={<CheckCircleIcon />}
                >
                    Save All Branding Changes
                </Button>
            </Box>
        </Container>
    );
};

export default CustomBranding;